import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  IconButton,
  Tooltip,
  Popper,
  Fade,
  Card as MuiCard,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Divider,
  Rating,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEvents as EmojiEventsIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import themes from '../theme/themes';

// ===== مكون بطاقة الإحصاءات =====
const StatsCard = ({ icon, title, value, subtitle, color, loading }) => {
  const { t } = useTranslation();
  return (
    <Card sx={{ 
      p: 2.5, 
      borderRadius: 3, 
      height: '100%',
      background: (theme) => `linear-gradient(135deg, ${alpha(color, 0.08)}, ${alpha(color, 0.02)})`,
      border: (theme) => `1px solid ${alpha(color, 0.15)}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: (theme) => `0 12px 40px ${alpha(color, 0.15)}`,
      }
    }}>
      <CardContent sx={{ p: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={28} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h4" fontWeight={700} color={color} sx={{ mt: 0.5 }}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.15), color: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// ===== مكون صف الترتيب =====
const LeaderboardRow = ({ rank, captain }) => {
  const { t } = useTranslation();
  const getRankColor = (rank) => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    return '#1976D2';
  };

  return (
    <TableRow sx={{ 
      '&:hover': { bgcolor: alpha('#FF1493', 0.04) },
      bgcolor: rank <= 3 ? alpha(getRankColor(rank), 0.08) : 'transparent',
    }}>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          {rank <= 3 ? (
            <EmojiEventsIcon sx={{ color: getRankColor(rank) }} />
          ) : (
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              #{rank}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: alpha('#FF1493', 0.15) }}>
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {captain.name}
            </Typography>
            <Rating value={parseFloat(captain.rating) || 5} size="small" readOnly precision={0.5} />
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={600}>
          {captain.total_rides || 0}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip 
          label={`${captain.acceptance_rate || 0}%`}
          size="small"
          color={captain.acceptance_rate > 80 ? 'success' : captain.acceptance_rate > 50 ? 'warning' : 'error'}
          variant="outlined"
        />
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={600} color="primary.main">
          {captain.total_earnings || 0} {t('riyal') || 'ريال'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip 
          label={`${captain.response_time || 0} ث`}
          size="small"
          color={captain.response_time < 30 ? 'success' : captain.response_time < 60 ? 'warning' : 'error'}
          variant="outlined"
        />
      </TableCell>
    </TableRow>
  );
};

const PerformanceReport = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentTheme, changeTheme, allThemes } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [period, setPeriod] = useState('daily');
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPalette, setOpenPalette] = useState(false);
  
  const [performanceData, setPerformanceData] = useState({
    totalRequests: 0,
    acceptedRequests: 0,
    rejectedRequests: 0,
    avgResponseTime: 0,
    acceptanceRate: 0,
    completionRate: 0,
    totalEarnings: 0,
  });
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [periodStats, setPeriodStats] = useState({
    daily: { requests: 0, earnings: 0, responseTime: 0 },
    weekly: { requests: 0, earnings: 0, responseTime: 0 },
    monthly: { requests: 0, earnings: 0, responseTime: 0 },
  });

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/captains/performance/${user?.id}?period=${period}`);
      const data = response.data;
      
      setPerformanceData({
        totalRequests: data.performance?.total_requests || 0,
        acceptedRequests: data.performance?.accepted_requests || 0,
        rejectedRequests: data.performance?.rejected_requests || 0,
        avgResponseTime: data.performance?.avg_response_time || 0,
        acceptanceRate: data.performance?.acceptance_rate || 0,
        completionRate: data.performance?.completion_rate || 0,
        totalEarnings: data.performance?.total_earnings || 0,
      });
      
      setLeaderboard(data.leaderboard || []);
      setPeriodStats(data.periodStats || {
        daily: { requests: 0, earnings: 0, responseTime: 0 },
        weekly: { requests: 0, earnings: 0, responseTime: 0 },
        monthly: { requests: 0, earnings: 0, responseTime: 0 },
      });
      
    } catch (err) {
      console.error('❌ Error fetching performance data:', err);
      setError(err.response?.data?.error || t('errorLoadingData') || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [user, period, t]);

  useEffect(() => {
    if (user) {
      fetchPerformanceData();
    }
  }, [user, period]);

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPalette((prev) => !prev);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setOpenPalette(false);
  };

  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  return (
    <Box sx={{
      pb: 8,
      bgcolor: (theme) => theme.palette.background.default,
      minHeight: '100vh',
      transition: 'background 0.5s ease',
    }}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          bgcolor: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(16px)',
          borderBottom: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          transition: 'all 0.5s ease',
        }}
      >
        <Toolbar>
          <IconButton onClick={() => navigate('/captain')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            📊 {t('performanceReport') || 'تقرير الأداء'}
          </Typography>

          <Tooltip title={t('changeTheme') || 'تغيير الثيم'}>
            <IconButton onClick={handlePaletteClick} sx={{ mx: 1 }}>
              <PaletteIcon />
            </IconButton>
          </Tooltip>

          <Popper open={openPalette} anchorEl={anchorEl} placement="bottom-end" transition sx={{ zIndex: 9999 }}>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <MuiCard sx={{ p: 2, mt: 1, borderRadius: 3, maxWidth: 280, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>🎨 اختر الثيم المفضل</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {Object.values(allThemes).map((themeData) => (
                      <Button
                        key={themeData.id}
                        variant={currentTheme === themeData.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => { changeTheme(themeData.id); setOpenPalette(false); }}
                        sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                      >
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', mr: 1.5, background: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`, border: '2px solid rgba(255,255,255,0.3)' }} />
                        <span dir="ltr">{themeData.icon} {themeData.name}</span>
                      </Button>
                    ))}
                  </Box>
                </MuiCard>
              </Fade>
            )}
          </Popper>

          <ToggleButtonGroup value={language} exclusive onChange={handleLanguageChange} size="small" sx={{ mx: 1 }}>
            <ToggleButton value="ar">🇸🇦</ToggleButton>
            <ToggleButton value="en">🇬🇧</ToggleButton>
          </ToggleButtonGroup>

          <Button variant="outlined" size="small" onClick={logout} startIcon={<LogoutIcon />} sx={{ borderRadius: 50 }}>
            {t('logout') || 'تسجيل الخروج'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} action={<Button color="inherit" size="small" onClick={fetchPerformanceData}>{t('retry') || 'إعادة المحاولة'}</Button>}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            {/* إحصائيات الأداء */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  icon={<AccessTimeIcon />} 
                  title={t('totalRequests') || 'إجمالي الطلبات'} 
                  value={performanceData.totalRequests} 
                  color="#2196F3" 
                  loading={loading} 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  icon={<CheckCircleIcon />} 
                  title={t('acceptedRequests') || 'الطلبات المقبولة'} 
                  value={performanceData.acceptedRequests} 
                  subtitle={`${performanceData.acceptanceRate}%`}
                  color="#4CAF50" 
                  loading={loading} 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  icon={<CancelIcon />} 
                  title={t('rejectedRequests') || 'الطلبات المرفوضة'} 
                  value={performanceData.rejectedRequests} 
                  color="#F44336" 
                  loading={loading} 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  icon={<AccessTimeIcon />} 
                  title={t('avgResponseTime') || 'متوسط وقت الاستجابة'} 
                  value={`${performanceData.avgResponseTime} ث`} 
                  color="#FF9800" 
                  loading={loading} 
                />
              </Grid>
            </Grid>

            {/* إحصائيات الفترات */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                📈 {t('periodStats') || 'إحصائيات الفترات'}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                    <Typography variant="caption" color="text.secondary">📅 {t('today') || 'اليوم'}</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">{periodStats.daily.requests}</Typography>
                    <Typography variant="caption" color="text.secondary">{periodStats.daily.earnings} {t('riyal') || 'ريال'} • {periodStats.daily.responseTime} ث</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
                    <Typography variant="caption" color="text.secondary">📆 {t('thisWeek') || 'هذا الأسبوع'}</Typography>
                    <Typography variant="h5" fontWeight={700} color="secondary.main">{periodStats.weekly.requests}</Typography>
                    <Typography variant="caption" color="text.secondary">{periodStats.weekly.earnings} {t('riyal') || 'ريال'} • {periodStats.weekly.responseTime} ث</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, borderRadius: 3, border: (theme) => `1px solid ${alpha('#9C27B0', 0.1)}` }}>
                    <Typography variant="caption" color="text.secondary">📊 {t('thisMonth') || 'هذا الشهر'}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#9C27B0">{periodStats.monthly.requests}</Typography>
                    <Typography variant="caption" color="text.secondary">{periodStats.monthly.earnings} {t('riyal') || 'ريال'} • {periodStats.monthly.responseTime} ث</Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* ترتيب السائقات */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  🏆 {t('captainLeaderboard') || 'ترتيب السائقات'}
                </Typography>
                <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
                  <ToggleButton value="daily">{t('daily') || 'يومي'}</ToggleButton>
                  <ToggleButton value="weekly">{t('weekly') || 'أسبوعي'}</ToggleButton>
                  <ToggleButton value="monthly">{t('monthly') || 'شهري'}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: alpha('#FF1493', 0.05) }}>
                      <TableRow>
                        <TableCell>{t('rank') || '# التصنيف'}</TableCell>
                        <TableCell>{t('captain') || 'السائقة'}</TableCell>
                        <TableCell align="center">{t('totalRides') || 'عدد الرحلات'}</TableCell>
                        <TableCell align="center">{t('acceptanceRate') || 'نسبة القبول'}</TableCell>
                        <TableCell align="center">{t('totalEarnings') || 'الأرباح'}</TableCell>
                        <TableCell align="center">{t('responseTime') || 'سرعة الاستجابة'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaderboard.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">📭 {t('noDataFound') || 'لا توجد بيانات'}</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaderboard.map((captain, index) => (
                          <LeaderboardRow key={captain.id || index} rank={index + 1} captain={captain} />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default PerformanceReport;
